/**
 * #3338 ADMINPAGES: which catalog pages a page list, or a set of roles, does
 * not reach.
 *
 * A page is reachable when the route guard would let someone in: it is a
 * universal page, or some allowed_pages list passes `isPathAllowed` for it
 * (aliases and "*" included). A page that is in the sidebar catalog and in no
 * role's list is a shipped page nobody can open: the guard redirects and the
 * sidebar drops the link, with no error anywhere. That is how
 * /product-inventory shipped on 23 Sep 2026.
 *
 * Two readers: the Roles screen names the pages an enumerated admin list is
 * missing, and tests/catalog-page-reachability-3338.test.ts fails CI when a
 * catalog page is reachable by no role in the code defaults.
 */
import { isPathAllowed, isUniversalPage } from "./rbac";
import { ALL_PAGE_PATHS, SIDEBAR_SECTIONS } from "./sidebar-pages";

/** Catalog pages the list does not reach, in catalog order. A list holding "*" misses none. */
export function catalogPagesMissingFrom(
  pages: readonly string[],
  catalog: readonly string[] = ALL_PAGE_PATHS,
): string[] {
  const list = [...pages];
  return catalog.filter((path) => !isUniversalPage(path) && !isPathAllowed(path, list));
}

/**
 * Entries in a page list that reach no catalog page: retired paths, typos,
 * anything not a path. The carry rule keeps these on a row (#2558 KEEPALL),
 * so a catalog-shaped view must name them or they are invisible. "*" is not
 * one; an alias of a catalog page is not one.
 */
export function pagesOutsideCatalog(
  pages: readonly string[],
  catalog: readonly string[] = ALL_PAGE_PATHS,
): string[] {
  return [
    ...new Set(
      pages.filter(
        (entry) => entry !== "*" && !catalog.some((path) => isPathAllowed(path, [entry])),
      ),
    ),
  ];
}

export interface RolePageList {
  id: string;
  allowed_pages: readonly string[];
}

/** Catalog pages no role reaches, in catalog order. */
export function unreachableCatalogPages(
  roles: readonly RolePageList[],
  catalog: readonly string[] = ALL_PAGE_PATHS,
): string[] {
  return catalog.filter(
    (path) =>
      !isUniversalPage(path) &&
      !roles.some((role) => isPathAllowed(path, [...role.allowed_pages])),
  );
}

/** The sidebar label for a catalog path, or the path itself. */
export function catalogPageLabel(path: string): string {
  for (const section of SIDEBAR_SECTIONS) {
    const hit = section.pages.find((page) => !page.divider && page.path === path);
    if (hit) return hit.label;
  }
  return path;
}

/**
 * #3687 REFUSALNOTE: the sidebar label of the catalog page a pathname sits
 * on, or null when it sits on none. Longest route wins, so a nested page
 * (/admin/material-mapping) names itself, and a detail route (/projects/123)
 * names its index page. Live tab routes (`accessAliases`) and retired paths
 * (`aliases`) name the page they belong to. Never returns the path itself:
 * the refusal notice must not show a route.
 */
export function refusedPageLabel(pathname: string): string | null {
  let best: { label: string; len: number } | null = null;
  for (const section of SIDEBAR_SECTIONS) {
    for (const page of section.pages) {
      if (page.divider) continue;
      for (const route of [page.path, ...(page.accessAliases ?? []), ...(page.aliases ?? [])]) {
        if (!route || route === "/") continue;
        const hit = pathname === route || pathname.startsWith(route + "/");
        if (hit && (!best || route.length > best.len)) best = { label: page.label, len: route.length };
      }
    }
  }
  return best?.label ?? null;
}

/** The one line shown where the route guard lands a refused navigation. */
export function refusedPageNotice(pathname: string): string {
  const label = refusedPageLabel(pathname);
  return label ? `You do not have access to ${label}.` : "You do not have access to that page.";
}

/** #3687: location.state key the route guard's redirect carries the refused path under. */
export const REFUSED_FROM_KEY = "refusedFrom";

/** The refused path a guard redirect carried, or null for any other arrival. */
export function readRefusedFrom(state: unknown): string | null {
  if (!state || typeof state !== "object") return null;
  const v = (state as Record<string, unknown>)[REFUSED_FROM_KEY];
  return typeof v === "string" && v.startsWith("/") ? v : null;
}

/**
 * The same location state with the refusal key taken out: null when nothing
 * else was carried, so a cleared entry reads exactly like a plain arrival.
 */
export function withoutRefusedFrom(state: unknown): Record<string, unknown> | null {
  if (!state || typeof state !== "object") return null;
  const rest = { ...(state as Record<string, unknown>) };
  delete rest[REFUSED_FROM_KEY];
  return Object.keys(rest).length > 0 ? rest : null;
}
